import { useEffect, useState } from 'react';
import { AlertCircle, Info, MonitorSmartphone } from 'lucide-react';
import { apiService, type PlatformSliceDTO, type PlatformsResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { DonutChart, type DonutSlice } from './JobsCharts';
import { formatInstant, formatPercent } from './jobStats';
import { inputColor, inputLabel, platformColor, platformFamily, platformLabel, sectionError, shares } from './shopFactions';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';

function formatPlaytime(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = minutes / 60;
  return h < 100 ? `${h.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h` : `${Math.round(h).toLocaleString('fr-FR')} h`;
}

const players = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} joueur${n > 1 ? 's' : ''}`;

function platformSlices(rows: PlatformSliceDTO[], value: (r: PlatformSliceDTO) => number, sub?: (r: PlatformSliceDTO) => string): DonutSlice[] {
  return rows.map((r) => ({ key: r.key, label: platformLabel(r.key), value: value(r), color: platformColor(r.key), sub: sub?.(r) }));
}

/**
 * Répartition des joueurs par plateforme (PC, mobiles, consoles) et par mode de jeu, dans la section Activité, pour
 * tous les projets : en ligne maintenant, et sur la période choisie en haut de la section (joueurs vus et temps de
 * jeu). La plateforme vient du proxy à la connexion ; elle n'est enregistrée que depuis le satellite 1.0.26.
 */
export function PlatformsBlock({ days }: { days: number }) {
  const gameId = useGameStore((s) => s.activeGameId);
  const groupKey = useActiveGameGroup()?.key ?? null;
  const [data, setData] = useState<PlatformsResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    void apiService.getPlatformStats(Math.min(365, Math.max(1, days))).then((res) => {
      if (!alive) return;
      if (res.data) { setData(res.data); setError(null); }
      else setError({ status: res.status, message: sectionError(res.status, res.error, 'la répartition par plateforme') });
    });
    return () => { alive = false; };
  }, [days, gameId, groupKey]);

  const family = data ? (() => {
    const s = shares(data.period.map((r) => r.players));
    const out: Record<string, number> = { pc: 0, mobile: 0, console: 0, autre: 0 };
    data.period.forEach((r, i) => { out[platformFamily(r.key)] += s[i] ?? 0; });
    return out;
  })() : null;

  return (
    <div className={styles.section}>
      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><MonitorSmartphone size={18} /> Plateformes des joueurs</h3>
          {data && <span className={lb.tableCount}>sur {data.days} jour{data.days > 1 ? 's' : ''}</span>}
        </div>
        {error ? (
          error.status === 404
            ? <div className={styles.info}><Info size={16} /><span>{error.message}</span></div>
            : <div className={lb.errorBox}><AlertCircle size={16} /><span>{error.message}</span></div>
        ) : !data ? <div className={lb.state}><div className={lb.spinner} /></div> : (
          <>
            {family && data.period.length > 0 && (
              <div className={styles.statLine}>
                <span>PC <b>{formatPercent(family.pc)}</b></span>
                <span>Mobile <b>{formatPercent(family.mobile)}</b></span>
                <span>Console <b>{formatPercent(family.console)}</b></span>
                {family.autre > 0 && <span>Autre ou inconnue <b>{formatPercent(family.autre)}</b></span>}
              </div>
            )}
            <div className={styles.grid2}>
              <div>
                <h4 className={styles.blockSub}>En ligne maintenant</h4>
                {data.onlineTotal === 0
                  ? <p className={lb.empty}>Personne en ligne sur ce serveur pour le moment.</p>
                  : <DonutChart slices={platformSlices(data.online, (r) => r.players)} formatValue={players}
                      centerValue={data.onlineTotal.toLocaleString('fr-FR')} centerLabel="en ligne" ariaLabel="Joueurs en ligne par plateforme" />}
              </div>
              <div>
                <h4 className={styles.blockSub}>Joueurs vus sur la période</h4>
                {data.period.length === 0
                  ? <p className={lb.empty}>Pas encore de plateforme enregistrée sur cette période.</p>
                  : <DonutChart slices={platformSlices(data.period, (r) => r.players, (r) => formatPlaytime(r.minutes))}
                      formatValue={players} centerValue={data.period.reduce((s, r) => s + r.players, 0).toLocaleString('fr-FR')}
                      centerLabel="joueurs" ariaLabel="Joueurs vus sur la période, par plateforme" />}
              </div>
              <div>
                <h4 className={styles.blockSub}>Temps de jeu par plateforme</h4>
                {data.period.length === 0 ? <p className={lb.empty}>-</p> : (
                  <DonutChart slices={platformSlices(data.period, (r) => r.minutes)} formatValue={formatPlaytime}
                    centerValue={formatPlaytime(data.period.reduce((s, r) => s + r.minutes, 0))} centerLabel="de jeu"
                    ariaLabel="Temps de jeu par plateforme" />
                )}
              </div>
              <div>
                <h4 className={styles.blockSub}>Mode de jeu (joueurs de la période)</h4>
                {data.periodInput.length === 0 ? <p className={lb.empty}>-</p> : (
                  <DonutChart
                    slices={data.periodInput.map((r) => ({ key: r.key, label: inputLabel(r.key), value: r.players, color: inputColor(r.key) }))}
                    formatValue={players} centerValue={data.periodInput.reduce((s, r) => s + r.players, 0).toLocaleString('fr-FR')}
                    centerLabel="joueurs" ariaLabel="Joueurs de la période par mode de jeu" />
                )}
              </div>
            </div>
            <p className={styles.muted}>
              Plateforme donnée par le jeu à la connexion (certificat Xbox, puis système déclaré s'il est inconnu).
              {data.since ? ` Enregistrée depuis le ${formatInstant(new Date(`${data.since}T00:00:00`).getTime()).split(' à ')[0]}.` : ' Pas encore enregistrée : le suivi commence avec le satellite 1.0.26.'}
              {data.untracked > 0 && ` ${data.untracked.toLocaleString('fr-FR')} joueur${data.untracked > 1 ? 's' : ''} de la période, vu${data.untracked > 1 ? 's' : ''} seulement avant le suivi, n'y figure${data.untracked > 1 ? 'nt' : ''} pas.`}
              {' '}Un joueur qui joue sur deux appareils compte dans les deux.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
